// B5 - Notification engine mocks (rules, templates, escalation, dispatch)

export interface NotifChannel {
  code: string
  name: string
  kind: 'web' | 'email' | 'sms' | 'push' | 'siren'
  status: 'online' | 'offline' | 'degraded'
  provider: string
  sentToday: number
  failureRate: number
}

export interface NotifTemplate {
  id: string
  code: string
  name: string
  category: 'alert' | 'approval' | 'reminder' | 'broadcast' | 'system'
  channels: string[]
  classification: 'public' | 'restricted' | 'secret'
  body: string
  active: boolean
  usedCount: number
}

export interface NotifRule {
  id: string
  code: string
  name: string
  trigger: string
  condition: string
  priority: 'low' | 'normal' | 'high' | 'critical'
  channels: string[]
  recipients: string
  active: boolean
  triggeredCount: number
}

export interface EscalationPolicy {
  id: string
  code: string
  name: string
  stages: number
  totalTimeoutMin: number
  active: boolean
}

export interface DispatchEvent {
  id: string
  ruleCode: string
  message: string
  severity: 'info' | 'warning' | 'critical'
  ts: string
  totalRecipients: number
  delivered: number
  failed: number
  acknowledged: number
  status: 'sending' | 'completed' | 'partial'
}

export const NOTIF_CHANNELS: NotifChannel[] = [
  { code: 'CH-WEB', name: 'Web (in-app)', kind: 'web', status: 'online', provider: 'PK-KQ Notification Service', sentToday: 1240, failureRate: 0.2 },
  { code: 'CH-EMAIL', name: 'Email', kind: 'email', status: 'online', provider: 'BQP SMTP Gateway', sentToday: 380, failureRate: 1.4 },
  { code: 'CH-SMS', name: 'SMS', kind: 'sms', status: 'online', provider: 'Viettel Military Network', sentToday: 124, failureRate: 0.8 },
  { code: 'CH-PUSH', name: 'Mobile Push', kind: 'push', status: 'online', provider: 'PK-KQ Mobile App', sentToday: 86, failureRate: 2.1 },
  { code: 'CH-SIREN', name: 'Loa báo động SCH', kind: 'siren', status: 'online', provider: 'Hệ thống loa 53 SCH', sentToday: 4, failureRate: 0.0 },
]

export const NOTIF_TEMPLATES: NotifTemplate[] = [
  { id: 't-1', code: 'TPL-ALERT-AIR', name: 'Cảnh báo mục tiêu lạ', category: 'alert', channels: ['CH-WEB', 'CH-PUSH', 'CH-SIREN'], classification: 'secret', body: 'Mục tiêu lạ {{trackId}} phát hiện tại {{location}}, độ cao {{alt}}m, hướng {{heading}}°.', active: true, usedCount: 28 },
  { id: 't-2', code: 'TPL-APP-KHB', name: 'Yêu cầu duyệt KHB', category: 'approval', channels: ['CH-WEB', 'CH-EMAIL'], classification: 'restricted', body: 'KHB {{code}} cần phê duyệt cấp {{level}}, hạn {{deadline}}.', active: true, usedCount: 240 },
  { id: 't-3', code: 'TPL-REMIND-KSK', name: 'Nhắc khám sức khỏe', category: 'reminder', channels: ['CH-WEB', 'CH-EMAIL', 'CH-SMS'], classification: 'restricted', body: 'Phi công {{name}} có lịch khám sức khỏe ngày {{date}}.', active: true, usedCount: 56 },
  { id: 't-4', code: 'TPL-WX-WARN', name: 'Cảnh báo thời tiết xấu', category: 'alert', channels: ['CH-WEB', 'CH-PUSH'], classification: 'restricted', body: 'Khí tượng cảnh báo {{kind}} tại {{region}} từ {{start}} đến {{end}}.', active: true, usedCount: 84 },
  { id: 't-5', code: 'TPL-BROADCAST-CMD', name: 'Phát lệnh chỉ huy QC', category: 'broadcast', channels: ['CH-WEB', 'CH-EMAIL', 'CH-SMS', 'CH-PUSH', 'CH-SIREN'], classification: 'secret', body: 'Lệnh từ Tư lệnh QC: {{message}}', active: true, usedCount: 12 },
  { id: 't-6', code: 'TPL-INC-ATB', name: 'Sự cố ATB', category: 'alert', channels: ['CH-WEB', 'CH-EMAIL', 'CH-PUSH'], classification: 'restricted', body: 'Sự cố {{code}} tại {{unit}}, mức độ {{severity}}.', active: true, usedCount: 18 },
  { id: 't-7', code: 'TPL-SYS-SYNC', name: 'Đồng bộ BQP thất bại', category: 'system', channels: ['CH-WEB', 'CH-EMAIL'], classification: 'restricted', body: 'Sync BQP thất bại: {{error}}', active: true, usedCount: 6 },
  { id: 't-8', code: 'TPL-LOG-ALERT', name: 'Cảnh báo tồn kho thấp', category: 'reminder', channels: ['CH-WEB', 'CH-EMAIL'], classification: 'restricted', body: 'Mặt hàng {{item}} sắp hết, còn {{qty}}.', active: true, usedCount: 38 },
]

export const NOTIF_RULES: NotifRule[] = [
  { id: 'r-1', code: 'RULE-INTRUDER', name: 'Mục tiêu lạ xâm nhập', trigger: 'A6 detect unknown', condition: 'classification = unknown AND in_restricted_zone', priority: 'critical', channels: ['CH-WEB', 'CH-PUSH', 'CH-SIREN'], recipients: '53 SCH + Tư lệnh QC + Sĩ quan trực', active: true, triggeredCount: 12 },
  { id: 'r-2', code: 'RULE-WX-CRIT', name: 'Thời tiết nguy hiểm', trigger: 'A5 alert', condition: 'severity = critical', priority: 'high', channels: ['CH-WEB', 'CH-PUSH'], recipients: 'Phi công + Trung tâm điều hành bay', active: true, triggeredCount: 28 },
  { id: 'r-3', code: 'RULE-KHB-DUE', name: 'KHB sắp quá hạn duyệt', trigger: 'WF SLA < 2h', condition: 'workflow = WF-KHB-3CAP AND remaining < 2h', priority: 'high', channels: ['CH-WEB', 'CH-EMAIL'], recipients: 'Approver hiện tại', active: true, triggeredCount: 86 },
  { id: 'r-4', code: 'RULE-LOW-FUEL', name: 'Nhiên liệu thấp', trigger: 'A7 fuel < 30%', condition: 'tank.current/capacity < 0.3', priority: 'high', channels: ['CH-WEB', 'CH-EMAIL'], recipients: 'Cán bộ HC + Chỉ huy', active: true, triggeredCount: 18 },
  { id: 'r-5', code: 'RULE-MEDICAL-EXP', name: 'GP y tế sắp hết hạn', trigger: 'A8 cert ≤30d', condition: 'days_to_expiry <= 30', priority: 'normal', channels: ['CH-WEB', 'CH-EMAIL', 'CH-SMS'], recipients: 'Phi công + BS HK', active: true, triggeredCount: 32 },
  { id: 'r-6', code: 'RULE-INC-CRIT', name: 'Sự cố ATB nghiêm trọng', trigger: 'A1 incident severity', condition: 'severity in (serious, accident)', priority: 'critical', channels: ['CH-WEB', 'CH-EMAIL', 'CH-PUSH', 'CH-SMS'], recipients: 'Toàn bộ chỉ huy', active: true, triggeredCount: 4 },
  { id: 'r-7', code: 'RULE-SYNC-FAIL', name: 'Sync BQP fail >3 lần', trigger: 'C4 sync error', condition: 'consecutive_fails >= 3', priority: 'normal', channels: ['CH-WEB', 'CH-EMAIL'], recipients: 'Admin BQP', active: true, triggeredCount: 2 },
  { id: 'r-8', code: 'RULE-FATIGUE', name: 'Phi công mệt mỏi cao', trigger: 'A8 fatigue > 70', condition: 'fatigue_score > 70 AND scheduled_today', priority: 'high', channels: ['CH-WEB', 'CH-EMAIL'], recipients: 'BS HK + Cmd phi công', active: true, triggeredCount: 6 },
]

export const ESCALATION_POLICIES: EscalationPolicy[] = [
  { id: 'e-1', code: 'ESC-INTRUDER', name: 'Leo thang mục tiêu lạ', stages: 4, totalTimeoutMin: 8, active: true },
  { id: 'e-2', code: 'ESC-WX-FLIGHT', name: 'Leo thang thời tiết bay', stages: 3, totalTimeoutMin: 30, active: true },
  { id: 'e-3', code: 'ESC-INC-CRIT', name: 'Leo thang sự cố nghiêm trọng', stages: 5, totalTimeoutMin: 15, active: true },
]

export const DISPATCH_EVENTS: DispatchEvent[] = [
  { id: 'd-1', ruleCode: 'RULE-INTRUDER', message: 'Mục tiêu lạ T-2026-0043 vùng SD-365', severity: 'critical', ts: '2026-05-06 08:28', totalRecipients: 53, delivered: 52, failed: 1, acknowledged: 47, status: 'partial' },
  { id: 'd-2', ruleCode: 'RULE-KHB-DUE', message: 'KHB-2026-0145 còn 2h chưa duyệt', severity: 'warning', ts: '2026-05-06 09:00', totalRecipients: 1, delivered: 1, failed: 0, acknowledged: 0, status: 'completed' },
  { id: 'd-3', ruleCode: 'RULE-WX-CRIT', message: 'Cảnh báo bão tại miền Trung', severity: 'critical', ts: '2026-05-06 06:45', totalRecipients: 124, delivered: 124, failed: 0, acknowledged: 118, status: 'completed' },
  { id: 'd-4', ruleCode: 'RULE-MEDICAL-EXP', message: '3 phi công có GP y tế sắp hết hạn', severity: 'info', ts: '2026-05-06 07:00', totalRecipients: 6, delivered: 6, failed: 0, acknowledged: 4, status: 'completed' },
  { id: 'd-5', ruleCode: 'RULE-LOW-FUEL', message: 'Tank BHA-03 còn 22% (JP-8)', severity: 'warning', ts: '2026-05-06 08:11', totalRecipients: 8, delivered: 8, failed: 0, acknowledged: 5, status: 'completed' },
]

export const NOTIF_KPIS = {
  channels: NOTIF_CHANNELS.length,
  channelsOnline: NOTIF_CHANNELS.filter((c) => c.status === 'online').length,
  templates: NOTIF_TEMPLATES.length,
  rules: NOTIF_RULES.length,
  rulesActive: NOTIF_RULES.filter((r) => r.active).length,
  sentToday: NOTIF_CHANNELS.reduce((s, c) => s + c.sentToday, 0),
  dispatchEvents: DISPATCH_EVENTS.length,
}
